import React from "react";
import { Card, CardContent, Typography, Button, Box, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  getActiveEntitlementEndDate,
  userHasActiveEntitlement,
  userHasPaidEntitlement,
} from "../../utils/entitlements";

const SubscriptionStatus = ({ user }) => {
  const paramsUrl = useParams();
  const { t, i18n } = useTranslation();
  let lang = paramsUrl.lang || i18n.language || "en";
  const navigate = useNavigate();

  if (!user) return null;

  const subscriptionActive = userHasActiveEntitlement(user);
  const paidActive = userHasPaidEntitlement(user);
  const subEnd = getActiveEntitlementEndDate(user);
  const trialEnd = user?.entitlements?.trial_active || user?.entitlements?.trial_expired
    ? new Date(user.entitlements.expires_at)
    : null;
  const now = new Date();
  const trialActive = Boolean(user?.entitlements?.trial_active && !paidActive && trialEnd && !Number.isNaN(trialEnd.getTime()) && trialEnd > now);
  const trialDaysRemaining = trialActive
    ? Math.max(1, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Νέο UI λογική
  return (
    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
      <Card
        sx={{
          minWidth: 320,
          maxWidth: 520,
          mx: "auto",
          boxShadow: 4,
          borderRadius: 4,
          background: (theme) =>
            subscriptionActive
              ? theme.palette.mode === "dark"
                ? "linear-gradient(90deg, #245e37 0%, #54b366 100%)"
                : "linear-gradient(90deg,#e7ffd5 0%,#c8facd 100%)"
              : theme.palette.mode === "dark"
              ? "linear-gradient(90deg, #2e3440 0%, #5e81ac 100%)"
              : "linear-gradient(90deg,#f1f7fa 0%,#e3ecee 100%)",
          color: theme => theme.palette.text.primary,
          p: { xs: 2, md: 3 }
        }}
      >
        <CardContent>
          {subscriptionActive ? (
            <>
              <Typography variant="h6" align="center" color="success.main" fontWeight={700} gutterBottom>
                {paidActive
                  ? (t("subscription_active") || "Premium subscription active")
                  : (lang === "el" ? "Δοκιμαστική περίοδος" : (t("trial") || "Trial"))}
              </Typography>
              <Typography align="center" sx={{ mb: 2 }}>
                {trialActive
                  ? (lang === "el"
                    ? `Απομένουν ${trialDaysRemaining} ημέρες`
                    : `${trialDaysRemaining} days left`)
                  : <>{t("subscription_active_until")} <b>{subEnd.toLocaleDateString(lang)}</b></>}
              </Typography>
              <Alert severity={trialActive ? "warning" : "success"} sx={{ mb: 2, textAlign: "center" }}>
                {trialActive
                  ? (lang === "el"
                    ? "Βρίσκεσαι σε δοκιμαστική περίοδο με πλήρη πρόσβαση μέχρι τη λήξη της."
                    : "You are on a trial period with full access until it ends.")
                  : (t("enjoy_premium_features") ||
                    "You have access to all premium features!")}
              </Alert>
            </>
          ) : (
            <>
              <Typography variant="h6" align="center" color="primary" fontWeight={700} gutterBottom>
                {lang === "el" ? "Δωρεάν" : (t("free_user_title") || "Free User")}
              </Typography>
              <Alert severity="info" sx={{ mb: 2, textAlign: "center" }}>
                {t("free_with_ads_info") ||
                  "You are on the Free plan. Upgrade to Premium for unlimited meal plans and advanced tools."}
              </Alert>
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => navigate(`/${lang}/subscription`)}
                >
                  {t("upgrade_to_premium") || "Upgrade to Premium"}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SubscriptionStatus;
